import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SITE, NAV } from "../lib/site";
import { Phone, MapPin, Mail, MessageCircle } from "lucide-react";

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  const goTo = (item) => {
    if (item.type === "route") {
      navigate(item.path);
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    if (!isHome) {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(item.id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
      return;
    }
    const el = document.getElementById(item.id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#064E3B] text-white pt-16 sm:pt-20 pb-10 overflow-hidden"
    >
      <div className="absolute inset-0 geo-overlay-light opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-16">
        {/* Verse */}
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D]">
            From the Qur'an
          </div>
          <p className="mt-4 sm:mt-5 font-serif-display text-xl sm:text-3xl leading-snug italic">
            "{SITE.verse.text}"
          </p>
          <p className="mt-3 text-[#FCD34D] text-xs sm:text-sm font-semibold tracking-wide">
            {SITE.verse.cite}
          </p>
        </div>

        <div className="mt-12 sm:mt-16 grid md:grid-cols-12 gap-8 sm:gap-10 pt-8 sm:pt-10 border-t border-white/15">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={SITE.logoUrl}
                alt="Alisaafaat"
                className="h-12 w-12 rounded-lg object-cover ring-1 ring-white/30"
              />
              <div>
                <div className="font-serif-display text-xl font-semibold">
                  Alisaafaat
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#FCD34D] font-semibold">
                  Welfare Foundation
                </div>
              </div>
            </div>
            <p className="mt-5 text-white/75 text-sm leading-relaxed max-w-md italic">
              "{SITE.mission}"
            </p>
            <p className="mt-2 text-white/60 text-xs">
              {SITE.vision}
            </p>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D] mb-5">
              Explore
            </div>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.id}>
                  <button
                    onClick={() => goTo(n)}
                    data-testid={`footer-nav-${n.id}`}
                    className="text-white/85 hover:text-white text-sm transition-colors"
                  >
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#FCD34D] mb-5">
              Reach us
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <Phone size={16} className="mt-0.5 text-[#FCD34D] shrink-0" />
                <a
                  href={`tel:${SITE.officePhone}`}
                  data-testid="footer-phone"
                  className="text-white/90 hover:text-white"
                >
                  {SITE.officePhone}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MessageCircle size={16} className="mt-0.5 text-[#FCD34D] shrink-0" />
                <a
                  href={SITE.whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp"
                  className="text-white/90 hover:text-white"
                >
                  {SITE.whatsapp}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Mail size={16} className="mt-0.5 text-[#FCD34D] shrink-0" />
                <a
                  href={`mailto:${SITE.email}`}
                  data-testid="footer-email"
                  className="text-white/90 hover:text-white break-all"
                >
                  {SITE.email}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={16} className="mt-0.5 text-[#FCD34D] shrink-0" />
                <div className="space-y-2 text-white/85">
                  {SITE.offices.map((o) => (
                    <p key={o}>{o}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/15 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <p>
            © {new Date().getFullYear()} Alisaafaat Welfare Foundation. All rights reserved.
          </p>
          <p className="italic">In service of the ummah, by the mercy of Allah.</p>
        </div>
      </div>
    </footer>
  );
}
